import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";
import { STATUSES } from "@/lib/utils";
import { MagnifyingGlass, UploadSimple, ArrowDown, ArrowUp, FileArrowUp, ArrowsClockwise, Broom, Trophy, ChartLineDown, Briefcase, CalendarDots, X } from "@phosphor-icons/react";
import SlaTimer from "@/components/SlaTimer";
import { motion } from "framer-motion";

export default function Leads() {
  const { user } = useAuth();
  const isGuest = user?.role === "guest";
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("received_at");
  const [order, setOrder] = useState("desc");
  const [viewMode, setViewMode] = useState("active"); // "active" | "won" | "lost"
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [dateRange, setDateRange] = useState({ from: "", to: "", label: "" });
  const [closedMatches, setClosedMatches] = useState(0);
  const [presets, setPresets] = useState(null);
  const fileRef = useRef(null);
  const reqIdRef = useRef(0);

  const fetchLeads = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { sort_by: sortBy, order };
      if (viewMode === "won") params.status = "won";
      else if (viewMode === "lost") params.status = "lost";
      else if (statusFilter !== "all") params.status = statusFilter;
      if (sourceFilter !== "all") params.source = sourceFilter;
      if (q.trim()) params.q = q.trim();
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      const { data } = await api.get("/leads", { params });
      if (reqId !== reqIdRef.current) return; // a newer request superseded this one
      // While searching, surface Won/Lost matches too — a hit hidden behind a
      // tab reads as "the lead doesn't exist" to the team.
      const searching = q.trim().length > 0;
      const filtered = viewMode === "active" && statusFilter === "all" && !searching
        ? data.filter((l) => l.status !== "won" && l.status !== "lost")
        : data;
      setLeads(filtered);
      setClosedMatches(searching && viewMode === "active" ? data.filter((l) => l.status === "won" || l.status === "lost").length : 0);
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      toast.error("Failed to load leads");
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line
  }, [statusFilter, sourceFilter, sortBy, order, viewMode, dateRange]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(fetchLeads, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  // Fetch fiscal-year quarter presets once (uses General settings)
  useEffect(() => {
    let cancelled = false;
    api.get("/settings/general")
      .then((r) => { if (!cancelled) setPresets(r.data?.quarter_presets || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const applyPreset = (preset, label) => {
    if (!preset) return;
    setDateRange({ from: preset.start, to: preset.end, label: label || preset.label });
  };
  const clearDateRange = () => setDateRange({ from: "", to: "", label: "" });

  const sources = useMemo(() => {
    const set = new Set();
    leads.forEach((l) => l.source && set.add(l.source));
    return Array.from(set).sort();
  }, [leads]);

  // Fetch assignable users so we can resolve department per assignee
  const [usersById, setUsersById] = useState({});
  useEffect(() => {
    let cancelled = false;
    api.get("/attorneys").then(({ data }) => {
      if (cancelled) return;
      const map = {};
      data.forEach((u) => {
        const key = (u.email || u.user_principal_name || "").toLowerCase();
        if (key) map[key] = u;
      });
      setUsersById(map);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const departmentsForLead = (lead) => {
    const emails = Array.isArray(lead.assigned_to) ? lead.assigned_to : (lead.assigned_to ? [lead.assigned_to] : []);
    const set = new Set();
    emails.forEach((e) => {
      const u = usersById[(e || "").toLowerCase()];
      if (u?.department) set.add(u.department);
    });
    return Array.from(set);
  };

  // "Last actioned" = the most recent meaningful event we know about for this
  // lead (status change / staff PATCH, first-response stamp, won/lost, or the
  // latest message in the thread). Falls back to created_at if none.
  const lastActionedAt = (lead) => {
    const candidates = [
      lead.updated_at,
      lead.first_response_at,
      lead.won_at,
      lead.lost_at,
    ];
    if (Array.isArray(lead.thread) && lead.thread.length > 0) {
      lead.thread.forEach((m) => m?.sent_at && candidates.push(m.sent_at));
    }
    const valid = candidates.filter(Boolean).map((s) => new Date(s).getTime()).filter((n) => !isNaN(n));
    if (valid.length === 0) return lead.created_at || null;
    return new Date(Math.max(...valid)).toISOString();
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/leads/upload-msg", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported: ${data.name || data.email_subject || "lead"}`);
      fetchLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/sync/mailbox");
      toast.success(`Synced — ${data.new_leads} new, ${data.merged_into_existing || 0} merged into existing`);
      fetchLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleDedupe = async () => {
    if (!window.confirm("Merge duplicate leads? Leads with the same phone, email, or subject will be combined into a single lead (keeping the oldest record). This cannot be undone.")) return;
    setDeduping(true);
    try {
      const { data } = await api.post("/leads/dedupe");
      toast.success(`Merged ${data.duplicates_merged} duplicate(s). ${data.remaining_leads} unique leads remain.`);
      fetchLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Dedupe failed");
    } finally {
      setDeduping(false);
    }
  };

  return (
    <div data-testid="leads-page" className="space-y-8 w-full">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="label-tracking">Pipeline</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none mt-2 text-slate-900">
            Leads
          </h1>
          <p className="text-sm text-slate-500 mt-3 max-w-2xl">
            {loading ? "…" : `${leads.length} lead${leads.length === 1 ? "" : "s"}`} in the pipeline.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".msg"
            className="hidden"
            onChange={handleUpload}
            data-testid="upload-file-input"
          />
          {!isGuest && (
            <>
              <Button
                data-testid="upload-msg-btn"
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="rounded-sm border-slate-300 gap-2 hover:bg-slate-900 hover:text-white hover:border-slate-900"
              >
                <FileArrowUp size={16} />
                {uploading ? "Importing…" : "Import .msg"}
              </Button>
              <Button
                data-testid="dedupe-btn"
                variant="outline"
                onClick={handleDedupe}
                disabled={deduping}
                className="rounded-sm border-slate-300 gap-2 hover:bg-slate-900 hover:text-white hover:border-slate-900"
                title="Merge duplicate leads (same phone, email, or subject)"
              >
                <Broom size={16} />
                {deduping ? "Merging…" : "Merge duplicates"}
              </Button>
              <Button
                data-testid="sync-mailbox-btn"
                onClick={handleSync}
                disabled={syncing}
                className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2"
              >
                <ArrowsClockwise size={16} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing…" : "Sync Mailbox"}
              </Button>
            </>
          )}
          {isGuest && (
            <span data-testid="guest-readonly-chip" className="px-3 py-1.5 text-xs rounded-sm bg-slate-100 text-slate-600 border border-slate-200">
              Read-only view
            </span>
          )}
        </div>
      </div>

      {/* Quarter / fiscal-year quick filter */}
      {presets && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="leads-quarter-presets">
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 mr-1">
            <CalendarDots size={12} /> Date filter:
          </span>
          <button type="button" data-testid="leads-preset-this-quarter" onClick={() => applyPreset(presets.this_quarter)} className={`px-2 py-0.5 rounded-sm text-[11px] border transition-colors ${dateRange.from === presets.this_quarter?.start ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white hover:bg-slate-900 hover:text-white hover:border-slate-900"}`}>{presets.this_quarter?.label}</button>
          <button type="button" data-testid="leads-preset-last-quarter" onClick={() => applyPreset(presets.last_quarter)} className={`px-2 py-0.5 rounded-sm text-[11px] border transition-colors ${dateRange.from === presets.last_quarter?.start ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white hover:bg-slate-900 hover:text-white hover:border-slate-900"}`}>{presets.last_quarter?.label}</button>
          <button type="button" data-testid="leads-preset-this-fy" onClick={() => applyPreset(presets.this_fiscal_year)} className={`px-2 py-0.5 rounded-sm text-[11px] border transition-colors ${dateRange.from === presets.this_fiscal_year?.start ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white hover:bg-slate-900 hover:text-white hover:border-slate-900"}`}>{presets.this_fiscal_year?.label}</button>
          <button type="button" data-testid="leads-preset-last-fy" onClick={() => applyPreset(presets.last_fiscal_year)} className={`px-2 py-0.5 rounded-sm text-[11px] border transition-colors ${dateRange.from === presets.last_fiscal_year?.start ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white hover:bg-slate-900 hover:text-white hover:border-slate-900"}`}>{presets.last_fiscal_year?.label}</button>
          <span className="mx-1 h-3 w-px bg-slate-200" />
          {(presets.quarters_current_fy || []).map((qtr) => (
            <button key={qtr.key} type="button" data-testid={`leads-preset-${qtr.key.toLowerCase()}`} onClick={() => applyPreset(qtr)} className={`px-2 py-0.5 rounded-sm text-[11px] border transition-colors ${dateRange.from === qtr.start ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white hover:bg-slate-900 hover:text-white hover:border-slate-900"}`}>{qtr.key}</button>
          ))}
          {dateRange.from && (
            <button
              type="button"
              data-testid="leads-preset-clear"
              onClick={clearDateRange}
              className="ml-2 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900"
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>
      )}

      {/* View tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
        <TabsList
          data-testid="leads-view-tabs"
          className="bg-transparent border-b border-slate-200 rounded-none h-auto p-0 gap-6 justify-start flex-wrap w-full"
        >
          <TabsTrigger
            value="active"
            data-testid="tab-active"
            className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] rounded-none px-0 pb-3 gap-2 label-tracking !text-xs data-[state=active]:text-slate-900 text-slate-500"
          >
            <Briefcase size={13} /> Active
          </TabsTrigger>
          <TabsTrigger
            value="won"
            data-testid="tab-won"
            className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 rounded-none px-0 pb-3 gap-2 label-tracking !text-xs data-[state=active]:text-emerald-700 text-slate-500"
          >
            <Trophy size={13} /> Won
          </TabsTrigger>
          <TabsTrigger
            value="lost"
            data-testid="tab-lost"
            className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-rose-600 rounded-none px-0 pb-3 gap-2 label-tracking !text-xs data-[state=active]:text-rose-700 text-slate-500"
          >
            <ChartLineDown size={13} /> Lost
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="lg:col-span-2 relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="leads-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone, subject, body, source…"
            className="pl-9 rounded-sm border-slate-300 h-10 focus-visible:ring-[#002FA7] focus-visible:ring-2"
          />
          {closedMatches > 0 && (
            <span
              data-testid="search-closed-matches-note"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-200"
              title="Search looks across every tab; Won/Lost matches are shown with their status badge"
            >
              incl. {closedMatches} won/lost
            </span>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter} disabled={viewMode !== "active"}>
          <SelectTrigger data-testid="status-filter" className="rounded-sm border-slate-300 h-10">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{viewMode === "active" ? "All active" : `All ${viewMode}`}</SelectItem>
            {STATUSES.filter((s) => viewMode === "active" ? s !== "won" && s !== "lost" : true).map((s) => (
              <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger data-testid="source-filter" className="rounded-sm border-slate-300 h-10">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {sources.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={`${sortBy}:${order}`} onValueChange={(v) => { const [s, o] = v.split(":"); setSortBy(s); setOrder(o); }}>
          <SelectTrigger data-testid="sort-select" className="rounded-sm border-slate-300 h-10">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="received_at:desc">Newest first</SelectItem>
            <SelectItem value="received_at:asc">Oldest first</SelectItem>
            <SelectItem value="name:asc">Name A → Z</SelectItem>
            <SelectItem value="name:desc">Name Z → A</SelectItem>
            <SelectItem value="source:asc">Source A → Z</SelectItem>
            <SelectItem value="status:asc">Status A → Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed" data-testid="leads-table">
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "14%" }} className="hidden sm:table-column" />
              <col style={{ width: "11%" }} className="hidden md:table-column" />
              <col style={{ width: "9%" }} className="hidden lg:table-column" />
              <col style={{ width: "9%" }} className="hidden lg:table-column" />
              <col style={{ width: "7%" }} />
              <col style={{ width: "10%" }} className="hidden md:table-column" />
              <col style={{ width: "10%" }} className="hidden lg:table-column" />
              <col style={{ width: "10%" }} className="hidden md:table-column" />
            </colgroup>
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="text-left p-4 label-tracking !tracking-[0.18em] font-medium whitespace-nowrap">Lead</th>
                <th className="text-left p-4 label-tracking !tracking-[0.18em] font-medium hidden sm:table-cell whitespace-nowrap">Contact</th>
                <th className="text-left p-4 label-tracking !tracking-[0.18em] font-medium hidden md:table-cell whitespace-nowrap">Source</th>
                <th className="text-left p-4 label-tracking !tracking-[0.18em] font-medium hidden lg:table-cell whitespace-nowrap">Assigned</th>
                <th className="text-left p-4 label-tracking !tracking-[0.18em] font-medium hidden lg:table-cell whitespace-nowrap">Department</th>
                <th className="text-left p-4 label-tracking !tracking-[0.18em] font-medium whitespace-nowrap">Status</th>
                <th className="text-left p-4 label-tracking !tracking-[0.18em] font-medium hidden md:table-cell whitespace-nowrap">Time to close</th>
                <th className="text-left p-4 label-tracking !tracking-[0.18em] font-medium hidden lg:table-cell whitespace-nowrap">Last actioned</th>
                <th className="text-right p-4 label-tracking !tracking-[0.18em] font-medium hidden md:table-cell whitespace-nowrap">Received</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-10 text-center text-slate-500">Loading…</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={9} className="p-10 text-center text-slate-500" data-testid="leads-empty">No leads match these filters.</td></tr>
              ) : (
                leads.map((lead, i) => (
                  <motion.tr
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2, delay: i * 0.02 }}
                    key={lead.id}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer align-top"
                    data-testid={`lead-row-${i}`}
                  >
                    <td className="p-4">
                      <Link to={`/leads/${lead.id}`} className="block min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-slate-900 truncate">{lead.name || "Unknown"}</span>
                          {Array.isArray(lead.thread) && lead.thread.length > 1 && (
                            <span
                              data-testid={`replies-chip-${i}`}
                              className="inline-flex items-center gap-1 text-[0.6rem] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 font-medium shrink-0 whitespace-nowrap"
                              title={`${lead.thread.length - 1} reply(ies) in this thread`}
                            >
                              {lead.thread.length - 1} {lead.thread.length - 1 === 1 ? "reply" : "replies"}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">{lead.summary || lead.email_subject}</div>
                      </Link>
                    </td>
                    <td className="p-4 text-slate-600 hidden sm:table-cell">
                      <Link to={`/leads/${lead.id}`} className="block min-w-0">
                        <div className="text-xs truncate">{lead.email || "—"}</div>
                        <div className="text-xs text-slate-500 font-mono truncate">{lead.phone || "—"}</div>
                      </Link>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <Link to={`/leads/${lead.id}`} className="block text-xs text-slate-700" title={lead.raw_source && lead.raw_source !== lead.source ? `Raw: ${lead.raw_source}` : (lead.source || "")}>
                        <span className="font-mono line-clamp-2">{lead.source || "—"}</span>
                        {lead.source && lead.source_canonical === false && (
                          <span
                            data-testid={`source-unmapped-${i}`}
                            className="inline-flex items-center gap-1 mt-1 text-[0.55rem] px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200 font-medium uppercase tracking-wider"
                            title="No canonical match — go to Settings → Sources to add an alias"
                          >
                            Unmapped
                          </span>
                        )}
                      </Link>
                    </td>
                    <td className="p-4 hidden lg:table-cell">
                      <Link to={`/leads/${lead.id}`} className="block text-xs text-slate-700">
                        {(() => {
                          const names = Array.isArray(lead.assigned_to_name) ? lead.assigned_to_name.filter(Boolean) : (lead.assigned_to_name ? [lead.assigned_to_name] : []);
                          if (names.length === 0) return <span className="text-slate-400">Unassigned</span>;
                          if (names.length === 1) return <span className="block truncate">{names[0]}</span>;
                          return <span className="block truncate" title={names.join(", ")}>{names[0]} <span className="text-slate-400">+{names.length - 1}</span></span>;
                        })()}
                      </Link>
                    </td>
                    <td className="p-4 hidden lg:table-cell" data-testid={`lead-dept-${i}`}>
                      <Link to={`/leads/${lead.id}`} className="text-xs">
                        {(() => {
                          const depts = departmentsForLead(lead);
                          if (depts.length === 0) return <span className="text-slate-400">—</span>;
                          return (
                            <span title={depts.join(", ")} className="inline-flex flex-wrap gap-1">
                              {depts.slice(0, 2).map((d) => (
                                <span key={d} className="inline-block px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 text-[0.65rem] whitespace-nowrap">
                                  {d}
                                </span>
                              ))}
                              {depts.length > 2 && <span className="text-slate-400 text-[0.65rem]">+{depts.length - 2}</span>}
                            </span>
                          );
                        })()}
                      </Link>
                    </td>
                    <td className="p-4">
                      <Link to={`/leads/${lead.id}`}><StatusBadge status={lead.status} /></Link>
                    </td>
                    <td className="p-4 hidden md:table-cell" data-testid={`lead-ttc-${i}`}>
                      <Link to={`/leads/${lead.id}`} className="inline-block">
                        {(lead.status === "won" || lead.status === "lost")
                          ? <span className="text-xs text-slate-400">—</span>
                          : <SlaTimer lead={lead} />}
                      </Link>
                    </td>
                    <td className="p-4 hidden lg:table-cell whitespace-nowrap" data-testid={`lead-last-actioned-${i}`}>
                      <Link to={`/leads/${lead.id}`} className="text-xs text-slate-600">
                        {relativeTime(lastActionedAt(lead))}
                      </Link>
                    </td>
                    <td className="p-4 text-right text-xs text-slate-500 hidden md:table-cell whitespace-nowrap">
                      <Link to={`/leads/${lead.id}`}>{relativeTime(lead.received_at)}</Link>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
